import { create } from "zustand";
import {
  BScanData,
  ImageLookup,
  ImageRegistration,
  ImageRegistrationTransform,
  LocalizerData,
  RegistrationPointPairs,
  RegistrationPoints,
} from '../types/ImagingTypes';
import {
  Coord,
  EditAction,
  ImageConfig,
  LayoutRow,
  LocUpdate,
  ResultRow,
} from "../types/LayoutTypes";
import cache from '@/cache';
import { studyConfig } from '../studyConfig';
import type { BsArmDir } from '../studyConfig';

interface WizardState {
  debug: boolean;

  cacheLoaded: boolean;
  allCaptureDates: string[];
  importCaptureDate: string;
  selectedCaptureDate: string;

  dbStats: {
    total: number;
    OD?: { [key: string]: number };
    OS?: { [key: string]: number };
  } | null;
  hasRequiredImages: boolean;

  // fields below go into localizer_data field in vvp_layouts table
  // ***
  studyEye: "OD" | "OS" | null;
  registrations: {
    OD: ImageRegistration | null;
    OS: ImageRegistration | null;
  }
  registrationPoints: RegistrationPoints;
  fovealPit: {
    OD: BScanData | null;
    OS: BScanData | null;
  };
  // ****

  binocularFixation: boolean | null;

  // chosen home BS arm per axis (only meaningful when studyConfig.homeBsArms is set)
  homeArmChoice: { vertical: BsArmDir; horizontal: BsArmDir };

  trainingEdits: {
    OD: EditAction[];
    OS: EditAction[];
  }
  localizerEdits: {
    OD: EditAction[];
    OS: EditAction[];
  }
  vascularCoords: {
    OD: Coord[];
    OS: Coord[];
  }
  prl: {
    OD: Coord;
    OS: Coord;
  }
  cyclo: {
    OD: number;
    OS: number;
  }
  trainingCSV: LayoutRow[];
  localizerCSV: LayoutRow[];
  homeCSV: LayoutRow[];
  localizerResult: ResultRow[];
}

interface WizardActions {
  setCacheLoaded: (cacheLoaded: boolean) => void;
  setAllCaptureDates: (captureDates: string[]) => void;
  setImportCaptureDate: (captureDate: string) => void;
  setSelectedCaptureDate: (captureDate: string) => void;


  setDbStats: (stats: WizardState['dbStats']) => void;
  setHasRequiredImages: (value: boolean) => void;

  setStudyEye: (studyEye: "OD" | "OS") => void;
  setOCTFovealPit: (eye, data: BScanData) => void;
  setImageRegistrations: (registrations: { OD: ImageRegistration|null, OS: ImageRegistration|null}) => void;
  setImageRegistrationPoints: (registrationPoints: RegistrationPoints) => void;

  setFixedLookup: (eye: string, lookup: ImageLookup) => void;
  setRegistration: (type: string, eye: string, transform: ImageRegistrationTransform) => void;
  setRegistrationPoints: (type: string, eye: string, pairs: RegistrationPointPairs | null) => void;

  setBinocularFixation: (value: boolean | null) => void;

  setHomeArmChoice: (choice: { vertical: BsArmDir; horizontal: BsArmDir }) => void;

  setTrainingEdits: (eye: string, trainingEdits: EditAction[]) => void;
  setLocalizerEdits: (eye: string, localizerEdits: EditAction[]) => void;
  setVascularCoords: (eye: string, vascularCoords: Coord[]) => void;
  setPRL: (eye: string, prl: Coord) => void;
  setCyclo: (eye: string, cyclo: number) => void;

  setTrainingCSV: (trainingCSV: LayoutRow[]) => void;
  setLocalizerCSV: (localizerCSV: LayoutRow[]) => void;
  setHomeCSV: (homeCSV: LayoutRow[]) => void;
  setLocalizerResult: (localizerResult: ResultRow[]) => void;
  buildLocalizerData: () => LocalizerData;

  getIsImagingReady: (eye: 'OD'|'OS') => boolean;

  reset: () => void;
}

const initialWizardState: WizardState = {
  debug: true,
  cacheLoaded: false,
  allCaptureDates: [],
  importCaptureDate: "",
  selectedCaptureDate: "",
  dbStats: null,
  hasRequiredImages: false,
  // imageUrls: null,
  studyEye: null,
  registrations: {
    OD: null,
    OS: null,
  },
  registrationPoints: {},
  fovealPit: {
    OD: null,
    OS: null,
  },
  binocularFixation: null,
  homeArmChoice: studyConfig.homeBsArms
    ? { vertical: studyConfig.homeBsArms.vertical.default, horizontal: studyConfig.homeBsArms.horizontal.default }
    : { vertical: 'S', horizontal: 'N' },
  trainingEdits: { OD: [], OS: [] },
  localizerEdits: { OD: [], OS: [] },
  vascularCoords: { OD: [], OS: [] },
  prl: { OD: new DOMPoint(), OS: new DOMPoint() },
  cyclo: { OD: 0, OS: 0 },
  trainingCSV: [],
  localizerCSV: [],
  homeCSV: [],
  localizerResult: [],
};

const useWizardStore = create<WizardState & WizardActions>()((set, get) => ({
  ...initialWizardState,

  setCacheLoaded: (cacheLoaded) => set({ cacheLoaded }),
  setAllCaptureDates: (allCaptureDates) => set({ allCaptureDates }),
  setImportCaptureDate: (importCaptureDate) => set({ importCaptureDate }),
  setSelectedCaptureDate: (selectedCaptureDate) => set({ selectedCaptureDate }),


  setDbStats: (dbStats) => set({ dbStats }),
  setHasRequiredImages: (hasRequiredImages) => set({ hasRequiredImages }),

  setStudyEye: (studyEye) => set({ studyEye }),
  setOCTFovealPit: (eye, data) => set(state => ({ fovealPit: { ...state.fovealPit, [eye]: data } })),
  setImageRegistrations: (registrations) => set({ registrations }),
  setImageRegistrationPoints: (registrationPoints) => set({ registrationPoints }),

  setFixedLookup: (eye, lookup) => {
    set(state => {
      const reg = { ...state.registrations };
      if (!reg[eye])
        reg[eye] = { FAF: getDefaultTransform(), FAFONH: getDefaultTransform(), FixedImage: null };
      reg[eye].FixedImage = lookup;
      return { registrations: reg };
    });
  },

  setRegistration: (type, eye, transform) => {
    set(state => {
      const reg = { ...state.registrations };
      if (!reg[eye])
        reg[eye] = { FAF: getDefaultTransform(), FAFONH: getDefaultTransform(), FixedImage: null };
      reg[eye][type] = transform;
      return { registrations: reg };
    });
  },

  setRegistrationPoints: (type, eye, pairs) => {
    set(state => {
      const pts = { ...state.registrationPoints };
      const eyePts = { ...pts[eye] };
      if (pairs)
        eyePts[type] = pairs;
      else
        delete eyePts[type];
      pts[eye] = eyePts;
      return { registrationPoints: pts };
    });
  },

  setBinocularFixation: (binocularFixation) => set({ binocularFixation }),

  setHomeArmChoice: (homeArmChoice) => set({ homeArmChoice }),

  setTrainingEdits: (eye, edits) => set(state => ({ trainingEdits: { ...state.trainingEdits, [eye]: edits } })),
  setLocalizerEdits: (eye, edits) => set(state => ({ localizerEdits: { ...state.localizerEdits, [eye]: edits } })),
  setVascularCoords: (eye, vascularCoords) => set(state => ({ vascularCoords: { ...state.vascularCoords, [eye]: vascularCoords } })),
  setPRL: (eye, prl) => set(state => ({ prl: { ...state.prl, [eye]: prl } })),
  setCyclo: (eye, cyclo) => set(state => ({ cyclo: { ...state.cyclo, [eye]: cyclo } })),

  setTrainingCSV: (trainingCSV) => set({ trainingCSV }),
  setLocalizerCSV: (localizerCSV) => set({ localizerCSV }),
  setHomeCSV: (homeCSV) => set({ homeCSV }),
  setLocalizerResult: (localizerResult) => set({ localizerResult }),
  buildLocalizerData: () => ({
    version: 2,
    timestamp: Math.trunc(Date.now() / 1000),
    captureDate: get().selectedCaptureDate,
    studyEye: get().studyEye,
    registrations: get().registrations,
    registration_points: get().registrationPoints,
    fovealPit: get().fovealPit,
    filenames: cache.imageCount < 1? [] : [...cache.images.OD.filenames, ...cache.images.OS.filenames],
  }),

  loadLocalizerData: () => {
  },

  getIsImagingReady: eye => get().hasRequiredImages && get().registrations[eye] && get().fovealPit[eye] && get().fovealPit[eye].scanX > 0,

  reset: () => set({ ...initialWizardState }),
}));


export const getDefaultTransform = () => ({
  translation: { x: 0, y: 0 },
  rotation: 0,
  scale: 1,
});


export default useWizardStore;
